import { Alert } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { filter, find, isNumber, map, omit, values } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import * as Yup from 'yup';

import { p2pProvidersApi } from 'api';
import { Math } from 'classes';
import {
  CloseFormikDialogResult,
  CloseDialogFormikHandler,
  Dialog,
  FormControls,
  FormikCheckbox,
  FormikNumericField,
  FormikYesNoRadioGroup,
  FormikSelect,
  Money,
} from 'components';
import {
  EnabledStatus,
  OrderStatus,
  OrderStatusDetails,
  OrderStatusReason,
  P2PProviderType,
  QueryKey,
} from 'enums';
import {
  useCurrencies,
  useMeta,
  usePrevious,
  useUser,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  P2PProvidersFilters,
  PayinOrder,
  ResolvePayinOrderDispute,
} from 'types';
import { formUtils, filtersUtils, orderUtils, validationUtils } from 'utils';

import { getReassignmentFees } from './reassignment-fees.utils';

type Values = ResolvePayinOrderDispute;

type Props = {
  open: boolean;
  order: PayinOrder;
  error?: AxiosError | null;
  amountDisabled: boolean;
  onClose: CloseDialogFormikHandler<Values>;
};

export const ResolveDisputeDialog: React.FC<Props> = ({
  open,
  order,
  error,
  amountDisabled,
  onClose,
}) => {
  const { isManager, isAdminOrTechOperator, isTrader } = useUser();
  const { t: tCommon } = useTranslation();
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.resolve',
  });
  const prevOpen = usePrevious(open);
  const { getFiatCurrencySymbol } = useCurrencies();
  const isTraderDispute = useMemo(() => !order.p2pProviderOrderId, [order]);
  const [initialValues, setInitialValues] = useState<Values>({
    status: OrderStatus.Completed,
    amount: order.amount,
    ...(isTraderDispute && { statusReason: '' as OrderStatusReason }),
    p2pProviderId: '',
    isRepayment: false,
  });
  const { orderStatusReasons, disputeCompletedStatusReasons } =
    useUserContext();

  const manualResolveProvidersFilters: P2PProvidersFilters = useMemo(
    () => ({
      type: P2PProviderType.ManualResolve,
      fiatCurrencyId: order.fiatCurrencyId,
      assetCurrencyId: order.p2pProviderOrder?.assetCurrencyId,
      payinEnabled: EnabledStatus.Enabled,
    }),
    [order.fiatCurrencyId, order.p2pProviderOrder?.assetCurrencyId],
  );

  const queryResultProviders = useQuery(
    [QueryKey.P2PProviders, manualResolveProvidersFilters],
    () =>
      p2pProvidersApi.getSummary({
        filters: filtersUtils.encode(
          JSON.stringify(manualResolveProvidersFilters),
        ),
      }),
    { enabled: isAdminOrTechOperator && open },
  );

  const manualResolveProviderOptions = useMemo(
    () =>
      formUtils.getOptions(
        filter(
          queryResultProviders.data,
          (p) => p.id !== order.p2pProviderOrder?.provider?.id,
        ),
      ),
    [order.p2pProviderOrder?.provider?.id, queryResultProviders],
  );

  const {
    traderCanCancelDispute,
    isDisputeRepaymentEnabled,
    isProviderIntervalCommissionsEnabled,
  } = useMeta();
  const preDisputeAmount = order.preDisputeAmount;
  const repaymentAvailable = useMemo(
    () =>
      isManager &&
      isDisputeRepaymentEnabled &&
      !amountDisabled &&
      isNumber(preDisputeAmount),
    [isManager, isDisputeRepaymentEnabled, amountDisabled, preDisputeAmount],
  );

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object().shape({
        status: Yup.mixed<OrderStatus>()
          .oneOf([OrderStatus.Completed, OrderStatus.Cancelled])
          .required(tCommon('errors.required')),
        amount: Yup.number()
          .moreThan(0, tCommon('errors.natural_number'))
          .required(tCommon('errors.required'))
          .when('isRepayment', {
            is: true,
            then: (schema) =>
              schema.moreThan(
                preDisputeAmount ?? 0,
                t('repayment_amount_too_small', { amount: preDisputeAmount }),
              ),
          }),
        statusReason: Yup.mixed<OrderStatusReason>()
          .oneOf(values(OrderStatusReason))
          .optional(),
        p2pProviderId: Yup.string().optional(),
        isRepayment: Yup.boolean().optional(),
      }),
    [t, tCommon, preDisputeAmount],
  );

  const currencySymbol = useMemo(
    () => getFiatCurrencySymbol(order?.fiatCurrencyId),
    [order, getFiatCurrencySymbol],
  );

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (!result.ok) {
        result.data?.formikHelpers.resetForm();
        onClose(result);
        return;
      }
      // the flag is sent only when checked: trader and other endpoints reject unknown fields
      const data = result.data;
      onClose(
        data && !data.values.isRepayment
          ? {
              ...result,
              data: { ...data, values: omit(data.values, 'isRepayment') },
            }
          : result,
      );
    },
    [onClose],
  );

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      handleClose({ ok: true, data: { values, formikHelpers } });
    },
    [handleClose],
  );

  const renderOrderStatus = useCallback(
    (status: string) => (
      <span>
        {`${t('resolve_as.prefix')} `}
        <span className="tw-font-bold">{status}</span>
      </span>
    ),
    [t],
  );

  const getStatusReasonOptions = useCallback(
    (status: OrderStatus) => {
      let statuses: OrderStatusReason[] = [];
      if (status === OrderStatus.Completed) {
        statuses = disputeCompletedStatusReasons;
      } else if (status === OrderStatus.Cancelled) {
        statuses = orderStatusReasons;
      }
      return map(statuses, (status) => ({
        label: orderUtils.getStatusReasonLabel(status),
        value: status,
      }));
    },
    [orderStatusReasons, disputeCompletedStatusReasons],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const statusReasonOptions = useMemo(
    () => getStatusReasonOptions(formik.values.status),
    [getStatusReasonOptions, formik.values.status],
  );

  const handleStatusChange = useCallback(() => {
    formik.setFieldValue('statusReason', '');
    formik.setFieldValue('p2pProviderId', '');
    formik.setFieldValue('isRepayment', false);
  }, [formik]);

  const handleRepaymentChange = useCallback(() => {
    formik.setFieldValue('statusReason', '');
  }, [formik]);

  const canRepay =
    repaymentAvailable && formik.values.status === OrderStatus.Completed;
  const isRepayment = canRepay && !!formik.values.isRepayment;

  useEffect(() => {
    if (open && !prevOpen) {
      setInitialValues({
        status: OrderStatus.Completed,
        amount: order.amount,
        ...(!order.p2pProviderOrderId
          ? { statusReason: '' as OrderStatusReason }
          : {}),
        p2pProviderId: '',
        isRepayment: false,
      });
    }
  }, [open, prevOpen, order]);

  useEffect(() => {
    if (error) {
      formik.setErrors(
        validationUtils.getFormErrors(error, {
          amount: { too_big: tCommon('errors.not_enough_money') },
        }),
      );
    } else {
      formik.setErrors({});
    }
  }, [tCommon, formik, error]);

  const canReassignProvider = useMemo(
    () =>
      !isTraderDispute &&
      // Reassignment may complete the order with a loss for the platform
      isAdminOrTechOperator &&
      formik.values.status === OrderStatus.Completed &&
      manualResolveProviderOptions.length > 0,
    [
      isTraderDispute,
      isAdminOrTechOperator,
      formik.values.status,
      manualResolveProviderOptions.length,
    ],
  );

  const reassignmentFees = useMemo(
    () =>
      canReassignProvider
        ? getReassignmentFees(
            order,
            find(queryResultProviders.data, {
              id: formik.values.p2pProviderId,
            }),
            isProviderIntervalCommissionsEnabled,
          )
        : null,
    [
      canReassignProvider,
      formik.values.p2pProviderId,
      isProviderIntervalCommissionsEnabled,
      order,
      queryResultProviders.data,
    ],
  );

  const repaymentDelta = useMemo(
    () =>
      new Math(Number(formik.values.amount) || 0).minus(preDisputeAmount ?? 0)
        .value,
    [formik.values.amount, preDisputeAmount],
  );
  const disputeCancelAvailable = useMemo(
    () =>
      isManager ||
      (isTrader &&
        traderCanCancelDispute &&
        !(
          order.statusDetails === OrderStatusDetails.DisputeRevertCancelled &&
          order.previousAmount
        )),
    [isManager, isTrader, traderCanCancelDispute, order],
  );

  return (
    <FormikProvider value={formik}>
      <Dialog
        open={open}
        title={t('title_dispute')}
        data={{ values: formik.values, formikHelpers: formik }}
        onClose={handleClose}
      >
        <Form>
          <Alert severity="info" sx={{ mb: 4, py: 0 }}>
            {formik.values.status === OrderStatus.Completed &&
              renderOrderStatus(t('resolve_as.completed'))}
            {disputeCancelAvailable &&
              formik.values.status === OrderStatus.Cancelled &&
              renderOrderStatus(t('resolve_as.cancelled'))}
          </Alert>
          {!!order.disputeAmount && (
            <div className="tw-mb-4">
              {`${tCommon('features.orders.table.columns.dispute_amount')}: `}
              <Money
                value={order.disputeAmount}
                fiatCurrencyId={order.fiatCurrencyId}
                symbol
              />
            </div>
          )}
          <FormControls>
            {disputeCancelAvailable && (
              <FormikYesNoRadioGroup
                name="status"
                label={t('payment_received')}
                yesValue={OrderStatus.Completed}
                noValue={OrderStatus.Cancelled}
                onChange={handleStatusChange}
              />
            )}
            {canRepay && (
              <FormikCheckbox
                name="isRepayment"
                label={t('is_repayment')}
                onChange={handleRepaymentChange}
              />
            )}
            {!order.p2pProviderOrderId &&
              !!statusReasonOptions.length &&
              !isRepayment && (
                <FormikSelect
                  name="statusReason"
                  label={t('reason')}
                  options={statusReasonOptions}
                  noneOption
                  disablePortal={false}
                />
              )}
            {formik.values.status === OrderStatus.Completed && (
              <FormikNumericField
                name="amount"
                disabled={amountDisabled}
                label={`${t('received_amount')} (${currencySymbol})`}
                allowNegative={false}
                required
                fullWidth
              />
            )}
            {isRepayment && repaymentDelta > 0 && (
              <div>
                {`${t('repayment_delta')}: `}
                <Money
                  value={repaymentDelta}
                  fiatCurrencyId={order.fiatCurrencyId}
                  symbol
                />
              </div>
            )}
            {canReassignProvider && (
              <FormikSelect
                name="p2pProviderId"
                label={t('manual_resolve_provider')}
                options={manualResolveProviderOptions}
                noneOption
                disablePortal={false}
              />
            )}
            {reassignmentFees && (
              <Alert
                severity={reassignmentFees.unprofitable ? 'warning' : 'info'}
              >
                {t(
                  reassignmentFees.unprofitable
                    ? 'reassign_fees.unprofitable'
                    : 'reassign_fees.profitable',
                  {
                    providerFee: reassignmentFees.providerFee,
                    agentsFee: reassignmentFees.agentsFee,
                    shopCommission: reassignmentFees.shopCommission,
                  },
                )}
              </Alert>
            )}
          </FormControls>
        </Form>
      </Dialog>
    </FormikProvider>
  );
};
